// Chat client + the in-game widget that sits under the next queue.
//
// Polls /api/chat while the widget is visible (4s cadence, paused on
// hidden tabs). Reading is public; sending requires a signed-in
// account. Channels: 'global' is live in the UI; 'match' is wired
// through the whole stack but its tab stays hidden until PvP ships —
// flip SHOW_MATCH_TAB when it does.

import { authHeader, getAccount } from './auth';
import { filterProfanity } from './profanity';

const POLL_MS = 4000;
const SHOW_MATCH_TAB = false;  // PvP later — API + plumbing already accept it
const COLLAPSE_KEY = 'settle:chat-collapsed';
/** Drop messages older than this from the UI on every poll. Server keeps
 *  24h on disk; the window is the chatting horizon — anything older isn't
 *  part of the active conversation and just takes up scroll space. */
const STALE_AFTER_MS = 2 * 60 * 60 * 1000;
/** Recheck for staleness independent of new messages, so a panel that's
 *  been idle for a while still sheds old rows. */
const STALE_SWEEP_MS = 60 * 1000;

export type ChatChannel = 'global' | `match:${string}`;
export interface ChatMessage { id: number; name: string; text: string; at: number }

async function fetchMessages(channel: ChatChannel, after: number): Promise<ChatMessage[] | null> {
  try {
    const qs = new URLSearchParams({ channel });
    if (after > 0) qs.set('after', String(after));
    const res = await fetch(`/api/chat?${qs.toString()}`);
    if (!res.ok) return null;
    const data = await res.json() as { ok: boolean; messages?: ChatMessage[] };
    return data.ok ? (data.messages ?? []) : null;
  } catch {
    return null;
  }
}

async function sendMessage(channel: ChatChannel, text: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeader() },
      body: JSON.stringify({ channel, text }),
    });
    const data = await res.json().catch(() => null) as { ok?: boolean; error?: string } | null;
    if (!res.ok || !data?.ok) {
      // A 403 with no JSON body is the network edge (WAF / bot
      // protection) intercepting the POST, not the chat API itself.
      const error = data?.error
        ?? (res.status === 403
          ? 'blocked at the network edge — the site may need a security exception for /api/'
          : `http ${res.status}`);
      return { ok: false, error };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network error' };
  }
}

/** The chat widget. Created once at boot (hidden); main.ts positions
 *  it under the queue per-frame and toggles visibility with the game
 *  state. Self-contained: owns its DOM, polling loop, and channel
 *  state. */
export interface ChatWidgetOpts {
  /** Display-side profanity filter state (live read). */
  profanityOn: () => boolean;
}

export class ChatWidget {
  readonly root: HTMLElement;
  private opts: ChatWidgetOpts;
  private list: HTMLElement;
  private input: HTMLInputElement;
  private sendBtn: HTMLButtonElement;
  private hint: HTMLElement;
  private channel: ChatChannel = 'global';
  private lastId = 0;
  private timer: number | null = null;
  private sweepTimer: number | null = null;
  private collapsed: boolean;
  private unavailable = false;

  constructor(opts: ChatWidgetOpts) {
    this.opts = opts;
    this.collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
    this.list = document.createElement('div');
    this.list.className = 'chat-list';
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = 300;
    this.input.className = 'chat-input';
    this.input.placeholder = 'say something…';
    this.input.spellcheck = false;
    this.sendBtn = document.createElement('button');
    this.sendBtn.type = 'button';
    this.sendBtn.className = 'chat-send';
    this.sendBtn.textContent = 'send';
    this.hint = document.createElement('div');
    this.hint.className = 'chat-hint';

    const header = document.createElement('div');
    header.className = 'chat-header';
    const title = document.createElement('span');
    title.className = 'chat-title';
    title.textContent = 'chat';
    const tabGlobal = document.createElement('button');
    tabGlobal.type = 'button';
    tabGlobal.className = 'chat-tab active';
    tabGlobal.textContent = 'global';
    header.appendChild(title);
    header.appendChild(tabGlobal);
    if (SHOW_MATCH_TAB) {
      const tabMatch = document.createElement('button');
      tabMatch.type = 'button';
      tabMatch.className = 'chat-tab';
      tabMatch.textContent = 'match';
      header.appendChild(tabMatch);
      // Channel switching lands here with PvP; setChannel() is ready.
    }
    const caret = document.createElement('button');
    caret.type = 'button';
    caret.className = 'chat-caret';
    caret.textContent = this.collapsed ? '▴' : '▾';
    caret.title = 'collapse chat';
    caret.addEventListener('click', () => {
      this.collapsed = !this.collapsed;
      caret.textContent = this.collapsed ? '▴' : '▾';
      this.root.classList.toggle('collapsed', this.collapsed);
      if (this.collapsed) this.root.style.height = 'auto';
      try { localStorage.setItem(COLLAPSE_KEY, this.collapsed ? '1' : '0'); } catch { /* */ }
    });
    header.appendChild(caret);

    const inputRow = document.createElement('div');
    inputRow.className = 'chat-input-row';
    inputRow.appendChild(this.input);
    inputRow.appendChild(this.sendBtn);

    this.root = document.createElement('div');
    this.root.id = 'chat-panel';
    this.root.classList.toggle('collapsed', this.collapsed);
    this.root.appendChild(header);
    this.root.appendChild(this.list);
    this.root.appendChild(inputRow);
    this.root.appendChild(this.hint);
    this.root.style.display = 'none';

    const trySend = async () => {
      const text = this.input.value.trim();
      if (!text) return;
      if (!getAccount()) {
        this.flashHint('sign in (main menu) to send messages');
        return;
      }
      this.input.value = '';
      const res = await sendMessage(this.channel, text);
      if (!res.ok) {
        this.flashHint(res.error === 'sending too fast' ? 'whoa — one message at a time' : `couldn't send: ${res.error}`);
        this.input.value = text;  // let them retry without retyping
      } else {
        void this.poll();  // show it immediately
      }
    };
    this.sendBtn.addEventListener('click', () => { void trySend(); });
    this.input.addEventListener('keydown', (e) => {
      // Enter sends; Escape returns focus to the game. stopPropagation
      // keeps game-global key handlers away while typing (input.ts
      // also defers to text fields, this is belt + suspenders).
      if (e.code === 'Enter') { e.preventDefault(); void trySend(); }
      else if (e.code === 'Escape') { e.preventDefault(); this.input.blur(); }
      e.stopPropagation();
    });
  }

  setChannel(c: ChatChannel): void {
    if (this.channel === c) return;
    this.channel = c;
    this.lastId = 0;
    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
    void this.poll();
  }

  /** Show + start polling (entering a run). Hidden entirely for
   *  under-13 accounts (child-safety; see the privacy policy). */
  show(): void {
    if (getAccount()?.over13 === false) { this.hide(); return; }
    if (this.root.style.display !== 'none') return;
    this.root.style.display = '';
    this.refreshPlaceholder();
    void this.poll();
    this.timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !this.unavailable) void this.poll();
    }, POLL_MS);
    // Sweep stale rows once a minute even without new traffic — keeps the
    // visible window fresh for a player who's been quiet but watching.
    this.sweepTimer = window.setInterval(() => this.sweepStale(), STALE_SWEEP_MS);
  }

  /** Hide + stop polling (back to menus). */
  hide(): void {
    if (this.root.style.display === 'none') return;
    this.root.style.display = 'none';
    if (this.timer != null) { clearInterval(this.timer); this.timer = null; }
    if (this.sweepTimer != null) { clearInterval(this.sweepTimer); this.sweepTimer = null; }
  }

  /** Re-anchor under the queue; called per-frame from App.onTick.
   *  maxHeight bounds the panel so it ends at the board's bottom edge;
   *  collapsed state ignores it (header-only). */
  position(left: number, top: number, width: number, maxHeight: number): void {
    this.root.style.left = `${Math.round(left)}px`;
    this.root.style.top = `${Math.round(top)}px`;
    this.root.style.width = `${Math.round(Math.max(170, width))}px`;
    this.root.style.height = this.collapsed ? 'auto' : `${Math.round(Math.max(150, maxHeight))}px`;
  }

  private refreshPlaceholder(): void {
    this.input.placeholder = getAccount() ? 'say something…' : 'sign in to chat';
  }

  private flashHint(text: string): void {
    this.hint.textContent = text;
    this.hint.classList.add('show');
    setTimeout(() => this.hint.classList.remove('show'), 2600);
  }

  private async poll(): Promise<void> {
    const msgs = await fetchMessages(this.channel, this.lastId);
    if (msgs == null) {
      // API missing (vite dev / no D1) — show a quiet note once and
      // stop hammering the endpoint.
      if (!this.unavailable) {
        this.unavailable = true;
        this.flashHint('chat unavailable right now');
      }
      return;
    }
    this.unavailable = false;
    // Always sweep stale entries; new entries may all be very recent but
    // existing ones could have aged out since the last poll.
    this.sweepStale();
    if (msgs.length === 0) return;
    const atBottom = this.list.scrollTop + this.list.clientHeight >= this.list.scrollHeight - 8;
    const horizon = Date.now() - STALE_AFTER_MS;
    for (const m of msgs) {
      this.lastId = Math.max(this.lastId, m.id);
      // Skip rendering if the server returned something already past our
      // staleness horizon (e.g. backfill after a long idle).
      if (m.at < horizon) continue;
      this.list.appendChild(this.buildRow(m));
    }
    // Cap the DOM at ~80 rows.
    while (this.list.children.length > 80) this.list.removeChild(this.list.firstChild!);
    if (atBottom) this.list.scrollTop = this.list.scrollHeight;
  }

  /** Build a single chat row: name + text + small right-aligned time
   *  stamp. The stamp updates relative formatting on each sweep so '2m
   *  ago' becomes '5m ago' without re-fetching. */
  private buildRow(m: ChatMessage): HTMLElement {
    const row = document.createElement('div');
    row.className = 'chat-msg';
    row.dataset.at = String(m.at);
    const name = document.createElement('span');
    name.className = 'chat-name';
    name.textContent = m.name;
    const text = document.createElement('span');
    text.className = 'chat-text';
    text.textContent = this.opts.profanityOn() ? filterProfanity(m.text) : m.text;
    const time = document.createElement('span');
    time.className = 'chat-time';
    time.textContent = formatChatTime(m.at);
    time.title = new Date(m.at).toLocaleString();
    row.appendChild(name);
    row.appendChild(text);
    row.appendChild(time);
    return row;
  }

  /** Remove any rendered row past the staleness horizon AND refresh the
   *  relative time stamps on rows that survive. */
  private sweepStale(): void {
    const horizon = Date.now() - STALE_AFTER_MS;
    const rows = Array.from(this.list.children) as HTMLElement[];
    for (const row of rows) {
      const at = parseInt(row.dataset.at ?? '0', 10);
      if (!at) continue;
      if (at < horizon) {
        row.remove();
        continue;
      }
      const time = row.querySelector('.chat-time') as HTMLElement | null;
      if (time) time.textContent = formatChatTime(at);
    }
  }
}

/** Compact 'just now / Xm ago / Xh ago / HH:MM' format for chat. Short
 *  enough to sit at the end of a message row without crowding the text. */
function formatChatTime(at: number): string {
  const dt = Date.now() - at;
  if (dt < 45_000) return 'now';
  if (dt < 3_600_000) return `${Math.round(dt / 60_000)}m`;
  if (dt < 24 * 3_600_000) return `${Math.floor(dt / 3_600_000)}h`;
  const d = new Date(at);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}
